import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, X, Camera } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar'
import { cn } from '@/lib/utils'

interface AvatarUploadProps {
    name: string
    avatarUrl?: string | null
    avatarColor?: string | null
    userId?: number | string
    onAvatarChange: (file: File | null) => void
    onColorChange?: (color: string) => void
    disabled?: boolean
    className?: string
}

export function AvatarUpload({
    name,
    avatarUrl,
    avatarColor,
    userId,
    onAvatarChange,
    onColorChange,
    disabled = false,
    className,
}: AvatarUploadProps) {
    const { t } = useTranslation()
    const [preview, setPreview] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Reset preview when avatarUrl prop changes
    useEffect(() => {
        if (avatarUrl === undefined || avatarUrl === null || avatarUrl === '') {
            setPreview(null)
        }
    }, [avatarUrl])

    const handleFileSelect = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert(t('userForm.selectImageFile'))
            return
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert(t('userForm.imageSizeLimit'))
            return
        }

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
            setPreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)

        onAvatarChange(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)

        const files = Array.from(e.dataTransfer.files)
        if (files.length > 0) {
            handleFileSelect(files[0])
        }
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleRemove = () => {
        setPreview(null)
        onAvatarChange(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleColorChange = () => {
        if (onColorChange) {
            // Generate a new random gradient color
            const colors = [
                'from-blue-500 to-purple-600',
                'from-green-500 to-teal-600',
                'from-pink-500 to-rose-600',
                'from-yellow-500 to-orange-600',
                'from-indigo-500 to-blue-600',
                'from-emerald-500 to-green-600',
                'from-violet-500 to-purple-600',
                'from-amber-500 to-yellow-600',
            ]
            const randomColor = colors[Math.floor(Math.random() * colors.length)]
            onColorChange(randomColor)
        }
    }

    const currentAvatarUrl = preview || (avatarUrl !== undefined ? avatarUrl : null)

    return (
        <div className={cn('space-y-4', className)}>
            {/* Avatar Display */}
            <div className="flex items-center space-x-4">
                <EnhancedAvatar
                    name={name || 'User'}
                    avatarUrl={currentAvatarUrl}
                    avatarColor={avatarColor}
                    userId={userId}
                    size="xl"
                />
                <div className="space-y-2">
                    <div className="text-sm font-medium">{t('userForm.profilePicture')}</div>
                    <div className="flex space-x-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled}
                        >
                            <Camera className="h-4 w-4 mr-2" />
                            {currentAvatarUrl ? t('userForm.change') : t('userForm.upload')}
                        </Button>
                        {currentAvatarUrl && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleRemove}
                                disabled={disabled}
                            >
                                <X className="h-4 w-4 mr-2" />
                                {t('userForm.remove')}
                            </Button>
                        )}
                        {!currentAvatarUrl && onColorChange && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleColorChange}
                                disabled={disabled}
                            >
                                {t('userForm.randomColor')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Drag & Drop Area (reduced height) */}
            <div
                className={cn(
                    'border-2 border-dashed rounded-lg px-4 py-3 text-center transition-colors',
                    isDragOver
                        ? 'border-primary bg-primary/5'
                        : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-xs text-muted-foreground mb-1">
                    {t('userForm.dragDropImage')}
                </p>
                <p className="text-xs text-muted-foreground">
                    PNG, JPG, GIF up to 5MB
                </p>
            </div>

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                        handleFileSelect(file)
                    }
                }}
                className="hidden"
                disabled={disabled}
            />
        </div>
    )
}
